import React, { useState, useEffect } from 'react';

interface ServerSettingsModalProps {
  visible: boolean;
  onClose: () => void;
  onSave: (ip: string) => Promise<boolean>;
}

export const ServerSettingsModal: React.FC<ServerSettingsModalProps> = ({
  visible,
  onClose,
  onSave,
}) => {
  const [ipInput, setIpInput] = useState('');
  const [testing, setTesting] = useState(false);
  const [status, setStatus] = useState<{ text: string; type: 'success' | 'error' | 'info' } | null>(null);

  useEffect(() => {
    if (visible) {
      if (typeof window !== 'undefined' && window.localStorage) {
        const savedIp = localStorage.getItem('custom_server_ip');
        if (savedIp) setIpInput(savedIp);
        else setIpInput('');
      }
      setStatus(null);
    }
  }, [visible]);

  if (!visible) return null;

  const handleTestAndSave = async () => {
    if (!ipInput.trim()) {
      setStatus({ text: 'Please enter a valid IP address or hostname', type: 'error' });
      return;
    }

    const ipPattern = /^([a-zA-Z0-9-.]+)|([0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3})$/;
    if (!ipPattern.test(ipInput.trim())) {
      setStatus({ text: 'Invalid IP address format (e.g. 192.168.1.15)', type: 'error' });
      return;
    }

    setTesting(true);
    setStatus({ text: 'Testing connection to server...', type: 'info' });

    const success = await onSave(ipInput.trim());
    setTesting(false);

    if (success) {
      setStatus({ text: 'Connected successfully! Settings saved.', type: 'success' });
      setTimeout(() => {
        onClose();
      }, 1500);
    } else {
      setStatus({ text: 'Connection failed. Verify server is running and on same Wi-Fi.', type: 'error' });
    }
  };

  const handleReset = async () => {
    setTesting(true);
    const success = await onSave('');
    setTesting(false);
    setIpInput('');
    setStatus({ text: 'Reset to default auto-resolved IP.', type: 'success' });
    setTimeout(() => {
      onClose();
    }, 1500);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(5, 8, 16, 0.85)', zIndex: 20000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
      <div style={{ width: '100%', maxWidth: '340px', backgroundColor: '#0F172A', borderRadius: '24px', border: '1px solid rgba(255, 255, 255, 0.1)', padding: '24px' }}>
        <h3 style={{ color: '#fff', fontSize: '16px', fontWeight: '900', letterSpacing: '1.5px', textAlign: 'center', marginBottom: '12px' }}>
          SERVER CONFIGURATION
        </h3>
        <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '12px', lineHeight: '1.5', textAlign: 'center', marginBottom: '20px' }}>
          Enter the local IP address of your developer Mac to connect the device over Wi-Fi.
        </p>

        <div style={{ backgroundColor: 'rgba(255, 255, 255, 0.03)', borderRadius: '12px', padding: '12px', border: '1px solid rgba(255, 255, 255, 0.05)', marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ color: 'rgba(255, 255, 255, 0.4)', fontSize: '11px', fontWeight: '700' }}>Host IP:</span>
          <span style={{ color: '#a78bfa', fontSize: '12px', fontWeight: '800' }}>{window.location.hostname}</span>
        </div>

        <label style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '10px', fontWeight: '800', marginBottom: '8px', letterSpacing: '1px', display: 'block' }}>
          SERVER IP ADDRESS
        </label>
        <input
          type="text"
          placeholder="e.g. 192.168.1.15"
          className="pwa-auth-input"
          style={{ marginBottom: '16px' }}
          value={ipInput}
          onChange={(e) => setIpInput(e.target.value)}
          disabled={testing}
        />

        {status && (
          <div style={{ fontSize: '12px', textAlign: 'center', marginBottom: '16px', fontWeight: '600', color: status.type === 'success' ? '#10b981' : status.type === 'error' ? '#ef4444' : '#3b82f6' }}>
            {status.text}
          </div>
        )}

        <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
          <button onClick={handleReset} disabled={testing} className="pwa-secondary-btn" style={{ flex: 1, marginTop: 0, padding: '12px' }}>
            RESET
          </button>
          <button onClick={handleTestAndSave} disabled={testing} className="pwa-primary-btn" style={{ flex: 1, marginTop: 0, padding: '12px' }}>
            {testing ? 'TESTING...' : 'TEST & SAVE'}
          </button>
        </div>

        <button onClick={onClose} disabled={testing} style={{ width: '100%', background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: '12px', fontWeight: '700', cursor: 'pointer', padding: '10px 0' }}>
          CANCEL
        </button>
      </div>
    </div>
  );
};
